// src/Redux/thunks/followsThunks.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosFollows } from "../../../api/axios";
import { FOLLOWS_PATHS } from "../../../api/endpoints";

const safeJson = (s) => {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
};

const asErr = (e) => {
  const sentPayload = e?.config?.data ? safeJson(e.config.data) : null;
  const err = {
    message: e?.response?.data?.message || e?.message || "Request failed",
    status: e?.response?.status,
    validationErrors: e?.response?.data?.errors,
    sentPayload,
  };
  console.error("❌ API Error:", err);
  return err.message;
};

const validateAndStringify = (value, fieldName) => {
  if (value === null || value === undefined || value === "") {
    throw new Error(`${fieldName} is required and cannot be empty`);
  }
  const v = String(value).trim();
  if (!v) throw new Error(`${fieldName} cannot be empty after trimming`);
  return v;
};

const normalizeFollowList = (raw) => {
  const data = raw?.data ?? raw;
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.following)) return data.following;
  if (Array.isArray(data.data?.following)) return data.data.following;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.items)) return data.items;
  return [];
};

const pickId = (obj) => {
  if (!obj) return "";
  const v =
    obj.followeeId ||
    obj.followee_id ||
    obj.doctorId ||
    obj.doctor_id ||
    obj.patientId ||
    obj.patient_id ||
    obj.doctor?.id ||
    obj.doctor?.doctorId ||
    obj.patient?.id ||
    obj.patient?.patientId ||
    obj.followee?.id ||
    obj.followee?.doctorId ||
    obj.followee?.patientId ||
    obj.followedDoctorId ||
    obj.followedDoctor?.id ||
    obj.followedPatientId ||
    obj.followedPatient?.id ||
    "";
  return String(v ?? "").trim();
};

const pickType = (obj) => {
  const type = (
    obj?.followeeType ||
    obj?.followee_type ||
    (obj?.doctor || obj?.doctorId ? "doctor" : null) ||
    (obj?.patient || obj?.patientId ? "patient" : null) ||
    "doctor"
  );
  return type === "user" ? "patient" : type;
};

const apiFolloweeType = (type) =>
  type === "patient" || type === "user" ? "user" : "doctor";

// ✅ force un shape stable pour le front : followeeId + followeeType + doctorId (compat)
const normalizeFollowItem = (item) => {
  const followeeId = pickId(item);
  const followeeType = pickType(item);
  return {
    ...item,
    followeeId,
    followeeType,
    doctorId: followeeType === "doctor" ? followeeId : item.doctorId, // compat DoctorHeaderCard / ancien code
  };
};

/**
 * ✅ FOLLOW
 * POST /follows
 *
 * ⚠️ IMPORTANT : le backend valide le body en whitelist stricte et rejette
 * tout champ inconnu avec l'erreur "property followerType should not exist".
 * NE JAMAIS envoyer `followerType` dans le payload, même si un hook front
 * le calcule pour ses propres besoins internes (affichage, règles locales).
 * Seuls `followerId`, `followeeId` et `followeeType` sont acceptés.
 */
export const followDoctorThunk = createAsyncThunk(
  "follows/followDoctor",
  async ({ followerId, followeeId, followeeType }, { rejectWithValue }) => {
    try {
      const followerIdStr = validateAndStringify(followerId, "followerId");
      const followeeIdStr = validateAndStringify(followeeId, "followeeId");
      const followeeTypeNorm = apiFolloweeType(followeeType);

      const payload = {
        followerId: followerIdStr,
        followeeId: followeeIdStr,
        followeeType: followeeTypeNorm,
      };

      const res = await axiosFollows.post(FOLLOWS_PATHS.FOLLOW, payload);

      return {
        ...(res.data?.data || res.data || {}),
        followerId: followerIdStr,
        followeeId: followeeIdStr,
        followeeType: followeeTypeNorm === "user" ? "patient" : "doctor",
      };
    } catch (e) {
      if (e.message && !e.response) return rejectWithValue(e.message);
      return rejectWithValue(asErr(e));
    }
  }
);

/**
 * ✅ GET FOLLOWED
 * GET /follows/:followerId
 */
export const getFollowedDoctorsThunk = createAsyncThunk(
  "follows/getFollowedDoctors",
  async ({ followerId, followeeType = "doctor" }, { rejectWithValue }) => {
    try {
      const followerIdStr = validateAndStringify(followerId, "followerId");
      const url = FOLLOWS_PATHS.GET_FOLLOWING(followerIdStr, followeeType);

      const res = await axiosFollows.get(url);

      const list = normalizeFollowList(res.data);
      return list.map(normalizeFollowItem);
    } catch (e) {
      if (e.message && !e.response) return rejectWithValue(e.message);
      return rejectWithValue(asErr(e));
    }
  }
);

/**
 * ✅ UNFOLLOW
 * DELETE /follows/:followerId/:followeeId
 *
 * Pas de body envoyé (DELETE), donc pas de risque de "should not exist" ici.
 */
export const unfollowDoctorThunk = createAsyncThunk(
  "follows/unfollowDoctor",
  async ({ followerId, followeeId, followeeType }, { rejectWithValue }) => {
    try {
      const followerIdStr = validateAndStringify(followerId, "followerId");
      const followeeIdStr = validateAndStringify(followeeId, "followeeId");

      const followeeTypeNorm = apiFolloweeType(followeeType);
      const url = FOLLOWS_PATHS.UNFOLLOW(
        followerIdStr,
        followeeIdStr,
        followeeTypeNorm,
      );

      await axiosFollows.delete(url);

      return {
        followerId: followerIdStr,
        followeeId: followeeIdStr,
        followeeType: followeeTypeNorm === "user" ? "patient" : "doctor",
      };
    } catch (e) {
      if (e.message && !e.response) return rejectWithValue(e.message);
      return rejectWithValue(asErr(e));
    }
  }
);